/**
 * feAI – Frontend
 * src/lib/apiClient.ts
 *
 * Axios instance pre-configured with the backend base URL from .env.
 * Automatically attaches the JWT Bearer token from localStorage if present.
 * On 401 responses, the user is automatically logged out and redirected to /login.
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || (import.meta.env.PROD ? '' : 'http://localhost:4000');

export const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 60_000, // 60 secondi – Partite Aperte Clienti richiede query pesanti
});

// ── Request interceptor – attach JWT if available ─────────────────────────────
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('feai_access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Response interceptor – surface error messages ────────────────────────────
// On 401 (token expired / invalid) the user is automatically logged out.
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Se il backend risponde 401, il token è scaduto o non valido → logout
    if (error.response?.status === 401) {
      localStorage.removeItem('feai_access_token');
      // Reindirizza al login se non ci si trova già
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    const message: string =
      error.response?.data?.message ??
      error.response?.data?.code ??
      error.message ??
      'Errore di rete';

    // Conserva lo status code sull'errore in modo che i chiamanti possano
    // distinguere gli errori transitori (500/502/503/504/network) da quelli
    // definitivi (401/404/400). Utile per il retry automatico del login SSO.
    const replyError = new Error(message) as Error & { status?: number; code?: string };
    if (error.response?.status) replyError.status = error.response.status;
    if (error.response?.data?.code) replyError.code = error.response.data.code;
    return Promise.reject(replyError);
  }
);
